import { Download, Star } from 'lucide-react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  getOrientation,
  type ImageDimensions,
} from '../../utils/imageDimensions';

interface DappPreviewShellProps {
  dappName: string;
  subtitle: string;
  description: string;
  bannerSlot?: ReactNode | null;
  iconSlot?: ReactNode | null;
  previewSlots: (ReactNode | null)[];
  previewDimensions?: ImageDimensions | null;
  title?: string;
}

/** White phone mock of the dApp Store detail page ("Listing Preview" in Figma). */
export function DappPreviewShell({
  dappName,
  subtitle,
  description,
  bannerSlot,
  iconSlot,
  previewSlots,
  previewDimensions,
  title = 'Listing Preview',
}: DappPreviewShellProps) {
  const hasIcon = iconSlot !== null && iconSlot !== undefined;
  const previewOrientation = previewDimensions
    ? getOrientation(previewDimensions)
    : 'portrait';

  const previewSlotSizeClass = !previewDimensions
    ? 'w-44 h-80'
    : previewOrientation === 'landscape'
      ? 'w-full'
      : previewOrientation === 'square'
        ? 'w-56'
        : 'w-44';
  const previewSlotStyle = previewDimensions
    ? {
        aspectRatio: `${previewDimensions.width} / ${previewDimensions.height}`,
      }
    : undefined;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="text-sm text-white">{title}</div>
      <div className="relative h-[667.5px] w-72 overflow-hidden rounded-[30px] bg-white">
        <div className="absolute left-0 top-0 h-36 w-72 bg-seeker-200">
          {bannerSlot}
        </div>

        <div
          className={twMerge(
            'absolute left-[15px] top-[120px] size-24 overflow-hidden rounded-3xl shadow-[inset_0px_1.40625px_1.40625px_0px_rgba(255,255,255,0.17)]',
            hasIcon ? 'bg-transparent' : 'bg-seeker-200',
          )}
        >
          {iconSlot}
        </div>
        <div className="absolute left-[190px] top-[180px] inline-flex items-center justify-center gap-1.5 rounded-3xl bg-gray-950 px-3 py-1.5">
          <Download className="size-4 text-white" aria-hidden="true" />
          <div className="text-center text-xs font-medium leading-normal text-white">
            Install
          </div>
        </div>

        <div
          title={dappName || undefined}
          className={twMerge(
            'absolute left-[15px] top-[222px] h-6 w-40',
            dappName
              ? 'truncate text-lg font-medium text-zinc-900'
              : 'rounded bg-seeker-200',
          )}
        >
          {dappName || ''}
        </div>
        <div
          title={subtitle || undefined}
          className={twMerge(
            'absolute left-[15px] top-[250px] h-4 w-52',
            subtitle
              ? 'truncate text-sm text-zinc-500'
              : 'rounded bg-seeker-200',
          )}
        >
          {subtitle || ''}
        </div>

        <div className="absolute left-[15px] top-[274.5px] inline-flex items-center gap-1.5 text-zinc-500">
          <div className="text-center text-sm font-medium leading-3">5.0</div>
          <div className="flex items-center gap-[0.75px]">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className="size-3 fill-current"
                aria-hidden="true"
              />
            ))}
          </div>
        </div>

        <div
          className={twMerge(
            'absolute left-[15px] top-[334px]',
            description ? 'text-xs leading-snug text-zinc-700' : '',
          )}
        >
          {description ? (
            <p className="line-clamp-4 w-64 whitespace-pre-wrap">
              {description}
            </p>
          ) : (
            <>
              <div className="mb-2 h-3 w-64 rounded bg-seeker-200" />
              <div className="mb-2 h-3 w-64 rounded bg-seeker-200" />
              <div className="mb-2 h-3 w-60 rounded bg-seeker-200" />
              <div className="h-3 w-28 rounded bg-seeker-200" />
            </>
          )}
        </div>

        <div className="absolute left-0 top-[428.5px] inline-flex w-72 items-center gap-2 overflow-hidden px-3">
          {previewSlots.map((previewSlot, index) => (
            <div
              key={index}
              style={previewSlotStyle}
              className={twMerge(
                'shrink-0 overflow-hidden rounded-2xl',
                previewSlotSizeClass,
                previewSlot ? 'bg-transparent' : 'bg-seeker-200',
              )}
            >
              {previewSlot}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
